import React, { useState, useEffect, useRef } from "react";
import { useFormContext } from "react-hook-form";
import { APIProvider, useMapsLibrary } from "@vis.gl/react-google-maps";
import { GOOGLE_MAPS_API_KEY } from "../../config";

// Google Maps must be loaded by one loader per page. The maps (e.g. the
// geofence editor on the same form) use @vis.gl's APIProvider, so the address
// box does too. A second loader (@googlemaps/js-api-loader) raced it and left
// google.maps.Map undefined when the map rendered.

// Join address parts into a single display string.
function formatAddress({
  street = "",
  city = "",
  state = "",
  country = "",
  zip = "",
} = {}) {
  return [street, city, state, country, zip].filter(Boolean).join(", ");
}

// Convert a Google PlaceResult into our address shape. Tolerant of missing
// pieces — always returns a full object, never throws.
function toAddress(place) {
  const components = place?.address_components || [];
  let streetNumber = "";
  let route = "";
  const out = { street: "", city: "", state: "", country: "", zip: "" };

  for (const c of components) {
    const types = c.types || [];
    if (types.includes("street_number")) streetNumber = c.long_name;
    else if (types.includes("route")) route = c.long_name;
    else if (types.includes("locality")) out.city = c.long_name;
    // Fallback for places (e.g. UK) that use postal_town instead of locality.
    else if (types.includes("postal_town") && !out.city) out.city = c.long_name;
    else if (types.includes("administrative_area_level_1")) out.state = c.long_name;
    else if (types.includes("country")) out.country = c.long_name;
    else if (types.includes("postal_code")) out.zip = c.long_name;
  }
  out.street = [streetNumber, route].filter(Boolean).join(" ");
  return out;
}

function SearchSiteInput(props) {
  return (
    <APIProvider apiKey={GOOGLE_MAPS_API_KEY}>
      <AddressAutocomplete {...props} />
    </APIProvider>
  );
}

function AddressAutocomplete({
  onAddressChange,
  onCoordinatesChange,
  name,
  label,
  defaultAddress,
  onReset,
  isAddMode,
  error,
}) {
  const { register, setValue } = useFormContext();
  const inputRef = useRef(null);
  const autocompleteRef = useRef(null);
  const [inputValue, setInputValue] = useState(
    defaultAddress ? formatAddress(defaultAddress) : ""
  );

  // Register the field with react-hook-form. The visible input is just a search
  // box; the authoritative value is the address object we set on selection.
  useEffect(() => {
    register(name);
  }, [register, name]);

  // Attach the Places Autocomplete widget once the library has loaded.
  const places = useMapsLibrary("places");
  useEffect(() => {
    if (!places || !inputRef.current) return;

    const autocomplete = new places.Autocomplete(inputRef.current, {
      fields: ["address_components", "geometry", "formatted_address"],
      types: ["address"],
      // No country restriction — global address search.
    });
    autocompleteRef.current = autocomplete;

    const listener = autocomplete.addListener("place_changed", () => {
      const place = autocomplete.getPlace();

      // Pressing Enter without choosing a suggestion (or a result with no
      // address detail) yields an empty/partial place. Ignore it rather than
      // crashing or wiping a valid existing address.
      if (!place || !place.address_components) return;

      const address = toAddress(place);
      setInputValue(place.formatted_address || formatAddress(address));
      setValue(name, address, { shouldValidate: true });
      if (typeof onAddressChange === "function") onAddressChange(address);

      // Surface coordinates so callers can seed a geofence center without a
      // separate geocoding round-trip.
      const loc = place.geometry?.location;
      if (loc && typeof onCoordinatesChange === "function") {
        onCoordinatesChange({ lat: loc.lat(), lng: loc.lng() });
      }
    });

    return () => {
      listener.remove();
      if (window.google?.maps?.event) {
        window.google.maps.event.clearInstanceListeners(autocomplete);
      }
    };
    // Callbacks are read at event time; re-attaching on every render would
    // stack Autocomplete widgets on the same input.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [places]);

  // When a default address arrives (edit mode), reflect it in the box and seed
  // the form value.
  useEffect(() => {
    if (defaultAddress) {
      setInputValue(formatAddress(defaultAddress));
      setValue(name, defaultAddress, { shouldValidate: true });
    }
  }, [defaultAddress, setValue, name]);

  // Clear the box when the form is reset into add mode.
  useEffect(() => {
    if (isAddMode) setInputValue("");
  }, [onReset, isAddMode]);

  // Stop Enter from submitting the form while a suggestion is highlighted (the
  // Places widget still handles the selection itself).
  const handleKeyDown = (event) => {
    if (
      event.key === "Enter" &&
      inputRef.current?.getAttribute("aria-activedescendant")
    ) {
      event.preventDefault();
    }
  };

  return (
    <div className="sm:col-span-3">
      <label className="block text-sm font-medium mb-1 text-gray-600 dark:text-gray-300">
        {label}
      </label>
      <input
        ref={inputRef}
        name={name}
        value={inputValue}
        placeholder="Enter an address"
        onChange={(e) => setInputValue(e.target.value)}
        onKeyDown={handleKeyDown}
        className="block w-full rounded-md border-0 px-3 py-2.5 text-gray-500 dark:text-gray-300 hover:text-gray-800 dark:hover:text-gray-100 hover:bg-gray-100 dark:hover:bg-white/[0.06] bg-gray-50 dark:bg-gray-800 focus:outline-none transition-colors duration-150 text-sm leading-none font-normal"
        style={{ outline: "none" }}
      />
      {error?.message && (
        <p className="mt-2 text-sm text-danger">{error.message}</p>
      )}
    </div>
  );
}

export { SearchSiteInput };
